import { Card } from "@/types";
import JourneyCard from "./JourneyCard";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import Input from "../Forms/Input";
import { useTranslations } from "@/contexts/Translation";
import throttle from "@/lib/throttle";
import Link from "../Link/Link";
import { useScreenshot } from "@/lib/useScreenshot";
import { CARD_PRESETS, isColorDark, type CardPreset } from "@/lib/journeyCardColors";
import styles from "./ScreenshotView.module.css";

type Colors = {
  fg: string;
  bg: string;
  canvas: string;
  isLightBg: boolean;
};

const ScreenshotView = ({ card }: { card: Card }) => {
  const t = useTranslations("screenshotView");
  const [image, takeScreenshot] = useScreenshot();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [colors, setColors] = useState<Colors>({
    fg: CARD_PRESETS[0].fg,
    bg: CARD_PRESETS[0].bg,
    canvas: CARD_PRESETS[0].canvas,
    isLightBg: !isColorDark(CARD_PRESETS[0].bg),
  });

  useEffect(() => {
    if (!image && cardRef.current) {
      takeScreenshot(cardRef.current);
    }
  }, [image, takeScreenshot]);

  const handleBlur = () => {
    throttle(() => {
      if (cardRef.current) {
        takeScreenshot(cardRef.current);
      }
    }, 1000);
  };

  const handleColorChange = (name: string, value: string) => {
    if (name === "bg") {
      const isLightBg = !isColorDark(value);
      setColors({ ...colors, [name]: value, isLightBg: isLightBg });
    } else {
      setColors({ ...colors, [name]: value });
    }
  };

  // Counts clicks on a colour pair, so a new screenshot is taken once the card is drawn in its colours.
  const [presetPicked, setPresetPicked] = useState(0);

  const handlePreset = (preset: CardPreset) => {
    setColors({ fg: preset.fg, bg: preset.bg, canvas: preset.canvas, isLightBg: !isColorDark(preset.bg) });
    setPresetPicked((n) => n + 1);
  };

  useEffect(() => {
    if (presetPicked && cardRef.current) {
      takeScreenshot(cardRef.current);
    }
  }, [presetPicked, takeScreenshot]);

  const isPresetActive = (preset: CardPreset) =>
    preset.bg === colors.bg && preset.fg === colors.fg && preset.canvas === colors.canvas;

  const previewStyle = {
    "--screenshot-canvas": colors.canvas || "#ffffff",
    "--screenshot-fg": colors.fg,
  } as CSSProperties;

  return (
    <div className={styles.modalContent}>
      <div className={styles.presets} role='group' aria-label={t("presetsTitle")}>
        <span className={styles.presetsTitle}>{t("presetsTitle")}</span>
        {CARD_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type='button'
            className={styles.preset}
            aria-pressed={isPresetActive(preset)}
            onClick={() => handlePreset(preset)}
          >
            <span className={styles.presetSwatch} style={{ backgroundColor: preset.bg, color: preset.fg, borderColor: preset.canvas }} aria-hidden='true'>
              Aa
            </span>
            {t(`preset_${preset.id}`)}
          </button>
        ))}
      </div>
      <Input
        className={[styles.colorField, styles.colorFieldOffset].join(" ")}
        fieldClassName={styles.colorInput}
        name='canvas'
        type='color'
        handleChange={(e: React.ChangeEvent<HTMLInputElement>) => handleColorChange("canvas", e.currentTarget.value)}
        label={t("labelCanvas")}
        value={colors.canvas}
      />

      <Input
        className={styles.colorField}
        fieldClassName={styles.colorInput}
        name='bg'
        type='color'
        handleChange={(e: React.ChangeEvent<HTMLInputElement>) => handleColorChange("bg", e.currentTarget.value)}
        handleBlur={handleBlur}
        label={t("labelBg")}
        value={colors.bg}
      />

      <Input
        className={styles.colorField}
        fieldClassName={styles.colorInput}
        name='fg'
        type='color'
        handleChange={(e: React.ChangeEvent<HTMLInputElement>) => handleColorChange("fg", e.currentTarget.value)}
        handleBlur={handleBlur}
        label={t("labelFg")}
        value={colors.fg}
      />
      {image && (
        <div className={styles.downloadLink}>
          <Link href={image} download={`journey-card.png`}>
            {t("downloadImage")}
          </Link>
        </div>
      )}
      <div className={styles.previewCanvas} style={previewStyle}>
        <JourneyCard screenshotView ref={cardRef} bgColor={colors.bg} fgColor={colors.fg} card={card} lightBg={colors.isLightBg} />
      </div>
    </div>
  );
};

export default ScreenshotView;
